import { delay, includes, isEmpty, isNil } from 'lodash';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useToggle } from 'react-use';

// import RepositionedFrontChat from 'components/repositioned-frontchat';
import { requestCallbackSelector } from 'containers/free-home-valuation/slice/selector';
import { requestCallbackThunk } from 'containers/free-home-valuation/slice/thunks';
import useMedia from 'hooks/useMedia';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { homeValuationSelector } from 'store/slices/homeValuation/selector';
import { Fields } from 'types/fields';
import scrollToTop from 'utils/scroll';
import CMASection from '../cma-section';
import messages from '../messages';
import { media } from './constants';
import Styled from './styles';

export interface ValuationResultProps {
  address: string;
  price: string | null;
  chartUrl: string;
  onEdit: () => void;
  homeValuationForm: UseFormReturn<Fields>;
  isWidgetError: boolean;
  callbackRequested: string[];
  cmaRequested: Record<string, string[]>;
  onCallbackRequest: (leadId: string) => void;
  onCMARequest: (leadId: string, email: string) => void;
}

export default function ValuationResult({
  address,
  price,
  chartUrl,
  onEdit,
  homeValuationForm,
  isWidgetError,
  callbackRequested,
  cmaRequested,
  onCallbackRequest,
  onCMARequest,
}: ValuationResultProps) {
  const dispatch = useAppDispatch();
  const { zohoHomeValuation } = useAppSelector(homeValuationSelector);
  const { data: requestCallbackData } = useAppSelector(requestCallbackSelector);
  const { isBelowSmallLaptop } = useMedia();
  const [isShowCMA, toggleCMA] = useToggle(false);

  const { id: leadId = '' } = zohoHomeValuation?.data || {};

  const isCallbackRequested = includes(callbackRequested, leadId);
  const isShowChart = !isWidgetError && !isEmpty(chartUrl);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (requestCallbackData?.success && !isCallbackRequested) {
      onCallbackRequest(leadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestCallbackData]);

  function onToggleCMA() {
    toggleCMA();

    if (isShowCMA) {
      const element = document.querySelector('.cma-toggle-cta');
      delay(() => element?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }

  function getCallback() {
    if (!isNil(leadId)) {
      dispatch(requestCallbackThunk({ leadId }));
    }
  }

  function renderContent() {
    if (isWidgetError) {
      return (
        <>
          <Styled.AddressErrorTitle>
            <FormattedMessage {...messages.addressErrorTitle} />
          </Styled.AddressErrorTitle>
          <Styled.AddressErrorSubtitle>
            <FormattedMessage {...messages.addressErrorSubtitle} />
          </Styled.AddressErrorSubtitle>
          <Styled.AddressErrorDescription>
            <FormattedMessage {...messages.addressErrorDescription} />
          </Styled.AddressErrorDescription>
          <Styled.ButtonContained
            className="get-callback-cta"
            disabled={isCallbackRequested}
            onClick={getCallback}
          >
            <FormattedMessage
              {...(isCallbackRequested
                ? messages.requestSent
                : messages.addressErrorCTA)}
            />
            {isCallbackRequested ? (
              <Image
                className="tick-icon"
                src={media.tick}
                alt="tick"
                width={20}
                height={20}
              />
            ) : (
              <Image src={media.phone} alt="phone" width={20} height={20} />
            )}
          </Styled.ButtonContained>
        </>
      );
    }

    return (
      <>
        <Styled.EstimationWrapper>
          <Styled.EstimationHeading>
            <FormattedMessage {...messages.estimationHeading} />
          </Styled.EstimationHeading>
          <Styled.EstimationPrice>{price}</Styled.EstimationPrice>
          <Styled.Divider />
          <Styled.EstimationNote>
            <FormattedMessage {...messages.estimationNote} />
          </Styled.EstimationNote>
        </Styled.EstimationWrapper>
        {isShowChart && (
          <Styled.ChartWrapper className="--desktop">
            <Image src={chartUrl} alt="Chart" width={326} height={140} />
          </Styled.ChartWrapper>
        )}
        <Styled.Note>
          <FormattedMessage {...messages.tenXNote} />
        </Styled.Note>
        <Link href="/listing">
          <Styled.ButtonContained>
            <FormattedMessage {...messages.startListingCTA} />
            <Image
              src={media.rightArrow}
              alt="Right Arrow"
              width={20}
              height={20}
            />
          </Styled.ButtonContained>
        </Link>
      </>
    );
  }

  function renderCMAContent() {
    return (
      <CMASection
        homeValuationForm={homeValuationForm}
        cmaRequested={cmaRequested}
        onCMARequest={onCMARequest}
      />
    );
  }

  function renderCMA() {
    if (isBelowSmallLaptop) {
      return (
        <>
          {isShowCMA && (
            <Styled.CMAContent>
              {isShowChart && (
                <>
                  <Styled.ChartHeading>
                    <FormattedMessage {...messages.chartHeading} />
                  </Styled.ChartHeading>
                  <Styled.ChartWrapper className="--tablet">
                    <Image
                      src={chartUrl}
                      alt="Chart"
                      width={326}
                      height={140}
                    />
                  </Styled.ChartWrapper>
                </>
              )}
              {renderCMAContent()}
            </Styled.CMAContent>
          )}

          <Styled.ButtonLink className="cma-toggle-cta" onClick={onToggleCMA}>
            <FormattedMessage
              {...(isShowCMA ? messages.showLessCTA : messages.showMoreCTA)}
            />
          </Styled.ButtonLink>
        </>
      );
    }

    return <Styled.CMAContent>{renderCMAContent()}</Styled.CMAContent>;
  }

  return (
    <Styled.Wrapper>
      <Styled.ResultWrapper>
        <Styled.LeftWrapper>
          <Styled.AddressWrapper>
            <Image
              src={isBelowSmallLaptop ? media.locationMobile : media.location}
              alt="Location"
              width={20}
              height={20}
            />
            <Styled.Address>{address}</Styled.Address>
            <Styled.ButtonLink onClick={onEdit}>
              <FormattedMessage {...messages.editCTA} />
            </Styled.ButtonLink>
          </Styled.AddressWrapper>
          {renderContent()}
        </Styled.LeftWrapper>
        <Styled.RightWrapper>
          {!isWidgetError && (
            <Styled.ChatBotNote>
              <FormattedMessage {...messages.chatBotNote} />
            </Styled.ChatBotNote>
          )}
          {/* <RepositionedFrontChat type="homeValuation" /> */}
        </Styled.RightWrapper>
      </Styled.ResultWrapper>
      <Styled.CMAWrapper>{renderCMA()}</Styled.CMAWrapper>
    </Styled.Wrapper>
  );
}
